/**
 * Maps raw Supabase / Postgres / network errors to translation keys.
 *
 * Returns a translation key (e.g. "errors.supabase.uniqueViolation") that the
 * caller resolves through the i18n layer, or null if the error is not recognized.
 * The `errorToast` wrapper in `./toast` then falls back to a contextual message
 * provided at the call site when this returns null.
 */

export type AppError = {
  code?: string;
  message?: string;
  status?: number;
  name?: string;
  [key: string]: unknown;
};

const POSTGRES_CODE_KEYS: Record<string, string> = {
  "23505": "errors.supabase.uniqueViolation",
  "23503": "errors.supabase.foreignKey",
  "23502": "errors.supabase.notNull",
  "23514": "errors.supabase.checkViolation",
  "42501": "errors.supabase.insufficientPrivilege",
  "PGRST301": "errors.supabase.rlsViolation",
};

const RPC_PATTERN_KEYS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /INSUFFICIENT_STOCK/i, key: "errors.supabase.insufficientStock" },
  { pattern: /INVOICE_ALREADY_PAID/i, key: "errors.supabase.invoiceAlreadyPaid" },
  { pattern: /EFFECTIVE_PRICE_BELOW_MIN/i, key: "errors.supabase.effectivePriceBelowMin" },
  { pattern: /PRICE_BELOW_MIN/i, key: "errors.supabase.priceBelowMin" },
  { pattern: /PRICE_ABOVE_MAX/i, key: "errors.supabase.priceAboveMax" },
  { pattern: /EMPLOYEE_PRICE_BELOW_MIN/i, key: "errors.supabase.employeePriceBelowMin" },
  { pattern: /EMPLOYEE_PRICE_ABOVE_MAX/i, key: "errors.supabase.employeePriceAboveMax" },
  // Lot A du 2026-09-27 : les deux refus `PT409` sur une facture encaissée.
  //
  // ⛔ SANS CES DEUX LIGNES, LE PROPRIÉTAIRE LIT UN TOAST GÉNÉRIQUE sur les
  //    refus centraux du chantier. `errorToast` consulte `mapSupabaseError`
  //    avant tout le reste et n'utilise JAMAIS `error.message` : un code inconnu
  //    retombe sur le message du point d'appel, ici « Impossible d'enregistrer
  //    la facture », qui ne dit ni pourquoi ni quoi faire.
  //
  // ⚠️ Les jetons du serveur sont en MINUSCULES, contrairement aux sept lignes
  //    ci dessus. Sans conséquence : ces motifs portent le drapeau `i`.
  //
  // ⛔ PIÈGE DE LECTURE DU COMMENTAIRE CI DESSUS, ET IL A TROMPÉ DEUX AGENTS LE
  //    2026-09-29. « `errorToast` n'utilise JAMAIS `error.message` » est vrai
  //    de l'AFFICHAGE : ce qui apparaît est la traduction de la clef, jamais la
  //    chaîne brute du serveur. Mais l'appariement, lui, LIT `err.message` :
  //    ces deux entrées vivent dans `RPC_PATTERN_KEYS`, que `mapSupabaseError`
  //    parcourt sur `err.message` faute d'avoir trouvé le code. Mesuré : sur le
  //    refus `invoice_correction_window_closed`, la personne lit bien la phrase
  //    qui nomme la sortie, et non un toast générique.
  //    ⛔ CONSÉQUENCE POUR LE SERVEUR : le jeton doit rester dans le `MESSAGE`
  //    de la `RAISE`. Le déplacer dans le `HINT` casserait l'appariement EN
  //    SILENCE, sans casser aucun test SQL, et le propriétaire retomberait sur
  //    le générique. C'est pour ça que le lot A met le jeton en `MESSAGE` et la
  //    phrase humaine en `HINT`, jamais l'inverse.
  { pattern: /person_booked_elsewhere/i, key: "errors.supabase.personBookedElsewhere" },
  { pattern: /invoice_has_settled_payments/i, key: "errors.supabase.invoiceHasSettledPayments" },
  {
    pattern: /invoice_correction_window_closed/i,
    key: "errors.supabase.invoiceCorrectionWindowClosed",
  },
  // La porte symétrique du même lot (2026-09-29) : le serveur refuse de faire
  // ENTRER de l'argent sur une facture annulée ou mise à la corbeille. Même
  // forme que les deux lignes ci dessus, `PT409` plus un jeton en minuscules.
  {
    pattern: /invoice_voided_takes_no_payment/i,
    key: "errors.supabase.invoiceVoidedTakesNoPayment",
  },
  // Annulation d'un RDV dont le brouillon de facture porte un lien vivant
  // (2026-10-02, revalidé par le déclencheur `revert_auto_invoice_on_appointment_cancel`).
  // ⛔ AVANT la ligne suivante : ce jeton CONTIENT `invoice_has_active_payment_link`,
  //    et le premier motif qui apparie gagne. Placée après, le personnel lirait
  //    « Annulez le lien avant de la modifier » à propos d'un rendez-vous.
  //    Le client, lui, lit sa propre phrase (`clientCancelRefusalKey`).
  {
    pattern: /appointment_invoice_has_active_payment_link/i,
    key: "errors.supabase.appointmentInvoiceHasActivePaymentLink",
  },
  // Lien de paiement vivant (2026-10-01) : la facture est figée côté serveur
  // tant qu'un client peut payer. InvoiceDialog l'intercepte pour proposer
  // l'annulation du lien ; les autres gestes (fusion, corbeille) lisent ceci.
  {
    pattern: /invoice_has_active_payment_link/i,
    key: "errors.supabase.invoiceHasActivePaymentLink",
  },
  // La troisième porte du même lot (2026-09-29, Ruling R83) : sur une facture
  // annulée ou mise à la corbeille, une suppression de lignes de paiement ne
  // peut pas faire MONTER son encaissement net.
  //
  // ⚠️ AUCUN ÉCRAN N'ATTEINT CE JETON AUJOURD'HUI, et je l'écris plutôt que de
  //    laisser croire le contraire. Balayage du 2026-09-29 : `src/` ne contient
  //    aucun `.delete()` sur `payments`, et le seul appelant serveur qui
  //    supprimait une ligne de remboursement (`cancel_customer_credit`) est
  //    expressément écarté du garde, parce qu'il ne touche que des lignes
  //    `store_credit`, qui ont leur propre gardien. Le geste que le garde refuse
  //    n'est donc atteignable que par un appel PostgREST direct.
  //    ⛔ L'entrée reste : un refus `PT409` qui arriverait sans mappage ferait
  //    lire « Impossible d'enregistrer la facture » à la place de la phrase
  //    nommée. Mesuré par leurre le 2026-09-29 : cette entrée retirée, le cas de
  //    `src/lib/__tests__/toast.invoiceRefusals.test.ts` rougit exactement sur
  //    ce repli. Le coût est de deux clefs i18n, l'assurance vaut plus.
  {
    pattern: /invoice_voided_refund_not_removable/i,
    key: "errors.supabase.invoiceVoidedRefundNotRemovable",
  },
  // Le GEL DU NET du même lot (2026-09-29, décision de Julien) : le serveur
  // refuse aussi qu'une suppression de lignes de paiement fasse BAISSER
  // l'encaissement net d'une facture annulée ou mise à la corbeille. Le net est
  // gelé dans les DEUX sens, et chaque sens a son jeton parce que la SORTIE
  // n'est pas la même : ici l'argent manquerait au tiroir, donc on renvoie vers
  // « Retirer du comptant » et non vers « Ajouter du comptant ».
  //
  // ⚠️ MÊME REMARQUE QUE POUR LE JETON VOISIN : aucun écran n'atteint ce jeton
  //    aujourd'hui, et je l'écris plutôt que de laisser croire le contraire.
  //    `src/` ne contient aucun `.delete()` sur `payments` ; le geste refusé
  //    n'est atteignable que par un appel PostgREST direct. L'entrée reste parce
  //    qu'un refus `PT409` sans mappage ferait lire « Impossible d'enregistrer
  //    la facture » à la place de la phrase nommée.
  {
    pattern: /invoice_voided_payment_not_removable/i,
    key: "errors.supabase.invoiceVoidedPaymentNotRemovable",
  },
  // La PORTE D'ORPHELIN DE TIROIR du même lot (2026-09-30, décision de Julien) :
  // le serveur refuse aussi une suppression qui laisse l'encaissement net d'une
  // facture annulée EXACTEMENT où il était mais déplace le tiroir, quand la
  // facture garde d'autres lignes de paiement. Deux jetons, parce que la sortie
  // est le MIROIR du mouvement que le lot emporte : un `cash_in` emporté renvoie
  // vers « Retirer du comptant », un `cash_out` emporté vers « Ajouter du
  // comptant ».
  //
  // ⛔ L'ORDRE NE PIÈGE PERSONNE ICI, et c'est vérifié : aucun des quatre jetons
  //    de ce garde n'est sous-chaîne d'un autre, donc aucun motif antérieur ne
  //    peut apparier l'un de ces deux là, ni l'inverse.
  //
  // ⚠️ MÊME REMARQUE QUE POUR LES DEUX JETONS VOISINS : aucun écran n'atteint
  //    ces jetons aujourd'hui, `src/` ne contient aucun `.delete()` sur
  //    `payments`, et le geste refusé n'est atteignable que par un appel
  //    PostgREST direct. Les entrées restent parce qu'un refus `PT409` sans
  //    mappage ferait lire « Impossible d'enregistrer la facture » à la place de
  //    la phrase nommée.
  {
    pattern: /invoice_voided_cash_in_not_removable/i,
    key: "errors.supabase.invoiceVoidedCashInNotRemovable",
  },
  {
    pattern: /invoice_voided_cash_out_not_removable/i,
    key: "errors.supabase.invoiceVoidedCashOutNotRemovable",
  },
  // La troisième porte des portes ouvertes (2026-10-01) : `hard_delete_invoice`
  // refuse d'effacer un brouillon qui porte de l'argent. Même forme que les
  // entrées ci dessus, `PT409` plus un jeton en minuscules dans le MESSAGE.
  {
    pattern: /invoice_draft_carries_payments/i,
    key: "errors.supabase.invoiceDraftCarriesPayments",
  },
];

/**
 * Codes d'erreur GoTrue, distincts des codes Postgres ci-dessus : ils vivent
 * dans le même champ `code` mais dans un autre espace de noms.
 *
 * `user_banned` est le refus posé par `deactivate-employee-account` quand un
 * salon archive un employé. GoTrue le renvoie AVANT même de vérifier le mot de
 * passe, donc le message qui en découle ne divulgue rien que l'API ne dise déjà.
 */
const AUTH_CODE_KEYS: Record<string, string> = {
  user_banned: "errors.supabase.userBanned",
};

const AUTH_MESSAGE_KEYS: Record<string, string> = {
  "Invalid login credentials": "errors.supabase.invalidCredentials",
  "Email not confirmed": "errors.supabase.emailNotConfirmed",
  "User already registered": "errors.supabase.userExists",
  "Email rate limit exceeded": "errors.supabase.tooManyRequests",
  // Filet pour un client qui renverrait le message sans le code ; le libellé
  // appartient à GoTrue et peut changer, d'où le code en premier.
  "User is banned": "errors.supabase.userBanned",
};

const AUTH_PATTERN_KEYS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /password\s+should\s+be\s+at\s+least\s+\d+\s+characters?/i, key: "errors.supabase.weakPassword" },
];

function isNetworkError(err: AppError): boolean {
  if (err.name === "NetworkError" || err.name === "TypeError") {
    if (typeof err.message === "string" && /network|failed to fetch|load failed/i.test(err.message)) {
      return true;
    }
  }
  if (err.status === 0) return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return false;
}

export function mapSupabaseError(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const err = error as AppError;

  // La contrainte non négative de `product_location_stock` est le filet derrière
  // la garde du déclencheur : si elle parle (ancienne base, chemin non couvert),
  // c'est bien un manque de stock, pas une « valeur invalide ».
  if (
    err.code === "23514" &&
    typeof err.message === "string" &&
    err.message.includes("product_location_stock_quantity_non_negative")
  ) {
    return "errors.supabase.insufficientStock";
  }

  // Les trois gardes de salon (paiement, dette, règlement) parlent en
  // check_violation : sans ce test, elles tomberaient sur le message
  // générique « valeur invalide », qui ne dit pas quoi faire.
  if (
    err.code === "23514" &&
    typeof err.message === "string" &&
    /_SALON_MISMATCH:/.test(err.message)
  ) {
    return "errors.supabase.salonMismatch";
  }

  if (typeof err.code === "string" && POSTGRES_CODE_KEYS[err.code]) {
    return POSTGRES_CODE_KEYS[err.code];
  }

  if (typeof err.code === "string" && AUTH_CODE_KEYS[err.code]) {
    return AUTH_CODE_KEYS[err.code];
  }

  if (typeof err.message === "string") {
    for (const { pattern, key } of RPC_PATTERN_KEYS) {
      if (pattern.test(err.message)) return key;
    }
    if (AUTH_MESSAGE_KEYS[err.message]) return AUTH_MESSAGE_KEYS[err.message];
    for (const { pattern, key } of AUTH_PATTERN_KEYS) {
      if (pattern.test(err.message)) return key;
    }
  }

  if (isNetworkError(err)) return "errors.supabase.networkError";

  return null;
}
